import IntranetLayout from "@/components/IntranetLayout";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useState } from "react";
import { Users, Plus, X, Calendar, Ship } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { useVessels, useCrew } from "@/hooks/useFleetData";

const statusBadge: Record<string, { label: string; variant: "default" | "secondary" | "destructive" | "outline" }> = {
  scheduled: { label: "Scheduled", variant: "secondary" },
  onboard: { label: "Onboard", variant: "default" },
  "on-leave": { label: "On Leave", variant: "outline" },
  completed: { label: "Completed", variant: "outline" },
};

const EmployeeCrewRoster = () => {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ crew_member_name: "", vessel_name: "", position: "", rotation_start: "", rotation_end: "", status: "scheduled", notes: "" });

  const { data: liveVessels = [] } = useVessels();
  const { data: liveCrew = [] } = useCrew();

  const { data: schedules = [], isLoading } = useQuery({
    queryKey: ["crew-schedules"],
    queryFn: async () => {
      const { data, error } = await supabase.from("crew_schedules").select("*").order("rotation_start", { ascending: true });
      if (error) throw error;
      return data;
    },
  });

  const createMutation = useMutation({
    mutationFn: async () => {
      const { error } = await supabase.from("crew_schedules").insert(form);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["crew-schedules"] });
      setShowForm(false);
      setForm({ crew_member_name: "", vessel_name: "", position: "", rotation_start: "", rotation_end: "", status: "scheduled", notes: "" });
      toast({ title: "Crew member added to roster" });
    },
  });

  const vessels = [...new Set(schedules.map((s) => s.vessel_name))];

  return (
    <IntranetLayout>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-display text-2xl text-foreground">Crew Roster</h1>
          <p className="text-sm text-muted-foreground font-body mt-1">Vessel assignments & rotation schedules</p>
        </div>
        <Button size="sm" variant={showForm ? "outline" : "default"} className={showForm ? "" : "bg-teal hover:bg-teal-light text-white"} onClick={() => setShowForm(!showForm)}>
          {showForm ? <><X className="w-4 h-4 mr-1" /> Cancel</> : <><Plus className="w-4 h-4 mr-1" /> Add Crew</>}
        </Button>
      </div>

      {showForm && (
        <Card className="mb-6">
          <CardContent className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {liveCrew.length > 0 ? (
                <Select value={form.crew_member_name} onValueChange={(v) => setForm({ ...form, crew_member_name: v })}>
                  <SelectTrigger><SelectValue placeholder="Select crew member" /></SelectTrigger>
                  <SelectContent>
                    {liveCrew.map((c) => <SelectItem key={c.id} value={c.full_name}>{c.full_name} — {c.rank}</SelectItem>)}
                  </SelectContent>
                </Select>
              ) : (
                <Input value={form.crew_member_name} onChange={(e) => setForm({ ...form, crew_member_name: e.target.value })} placeholder="Crew member name" />
              )}
              {liveVessels.length > 0 ? (
                <Select value={form.vessel_name} onValueChange={(v) => setForm({ ...form, vessel_name: v })}>
                  <SelectTrigger><SelectValue placeholder="Select vessel" /></SelectTrigger>
                  <SelectContent>
                    {liveVessels.map((v) => <SelectItem key={v.id} value={v.name}>{v.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              ) : (
                <Input value={form.vessel_name} onChange={(e) => setForm({ ...form, vessel_name: e.target.value })} placeholder="Vessel name" />
              )}
              <Input value={form.position} onChange={(e) => setForm({ ...form, position: e.target.value })} placeholder="Position (e.g. Master, Chief Engineer)" />
              <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="scheduled">Scheduled</SelectItem>
                  <SelectItem value="onboard">Onboard</SelectItem>
                  <SelectItem value="on-leave">On Leave</SelectItem>
                  <SelectItem value="completed">Completed</SelectItem>
                </SelectContent>
              </Select>
              <div><label className="text-xs text-muted-foreground font-body block mb-1">Rotation Start</label><Input type="date" value={form.rotation_start} onChange={(e) => setForm({ ...form, rotation_start: e.target.value })} /></div>
              <div><label className="text-xs text-muted-foreground font-body block mb-1">Rotation End</label><Input type="date" value={form.rotation_end} onChange={(e) => setForm({ ...form, rotation_end: e.target.value })} /></div>
            </div>
            <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Notes (optional)" rows={2} className="mt-4" />
            <Button onClick={() => createMutation.mutate()} disabled={!form.crew_member_name || !form.vessel_name || !form.position || !form.rotation_start || !form.rotation_end} className="mt-4 bg-teal hover:bg-teal-light text-white">
              Add to Roster
            </Button>
          </CardContent>
        </Card>
      )}

      {isLoading ? (
        <div className="flex justify-center py-20"><div className="w-8 h-8 border-2 border-teal/30 border-t-teal rounded-full animate-spin" /></div>
      ) : schedules.length === 0 ? (
        <Card><CardContent className="p-16 text-center"><Users className="w-12 h-12 text-muted-foreground/30 mx-auto mb-4" /><p className="text-muted-foreground font-body">No crew scheduled yet</p></CardContent></Card>
      ) : (
        <div className="space-y-8">
          {vessels.map((vessel) => (
            <div key={vessel}>
              <div className="flex items-center gap-3 mb-3">
                <Ship className="w-5 h-5 text-teal" />
                <h2 className="font-display text-lg text-foreground">{vessel}</h2>
              </div>
              <Card>
                <CardContent className="p-0">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Name</TableHead>
                        <TableHead>Position</TableHead>
                        <TableHead className="hidden md:table-cell">Rotation</TableHead>
                        <TableHead>Status</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {schedules.filter((s) => s.vessel_name === vessel).map((s) => {
                        const cfg = statusBadge[s.status] || statusBadge.scheduled;
                        return (
                          <TableRow key={s.id}>
                            <TableCell className="font-medium">{s.crew_member_name}</TableCell>
                            <TableCell className="text-muted-foreground">{s.position}</TableCell>
                            <TableCell className="text-muted-foreground hidden md:table-cell">
                              <div className="flex items-center gap-1.5 text-xs">
                                <Calendar className="w-3 h-3" />
                                {new Date(s.rotation_start).toLocaleDateString()} — {new Date(s.rotation_end).toLocaleDateString()}
                              </div>
                            </TableCell>
                            <TableCell><Badge variant={cfg.variant}>{cfg.label}</Badge></TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            </div>
          ))}
        </div>
      )}
    </IntranetLayout>
  );
};

export default EmployeeCrewRoster;
